import axios, { type AxiosInstance, type AxiosError } from 'axios'
import type { ApiError } from '@/types/jispman'

let _accessToken: string | null = null
let _refreshToken: string | null = null
let _onLogout: (() => void) | null = null
let _onTokens: ((t: { accessToken: string; refreshToken: string }) => void) | null = null
let _isRefreshing = false

/**
 * Requests parked while a token refresh is in flight.
 *
 * Peter, 2026-09-02: *"those links on dash work on refresh, after some time they lead no
 * where, like the app hangs."*
 *
 * This was the cause. The queue held only a `resolve` — there was no way to fail a parked
 * request. So when several calls got a 401 at once (the dashboard fires eight in parallel),
 * the first started a refresh and the other seven parked. If the refresh then FAILED, the
 * first was rejected and logged out and the other seven were **never settled at all**: not
 * resolved, not rejected, just permanently pending promises holding their `await`s open.
 *
 * The screen shows loading spinners that never resolve and never error, which is exactly
 * what "the app hangs" looks like — and it only happens once the access token has expired,
 * which is why it worked immediately after a refresh and stopped working "after some time".
 *
 * Both outcomes are now delivered to everyone waiting.
 */
interface RefreshWaiter {
  resolve: (token: string) => void
  reject: (err: unknown) => void
}
let _refreshSubscribers: RefreshWaiter[] = []

function subscribeToRefresh(waiter: RefreshWaiter) {
  _refreshSubscribers.push(waiter)
}

function notifySubscribers(token: string) {
  // Drain BEFORE calling out: a waiter that itself triggers another request must not see a
  // queue that still contains itself.
  const waiting = _refreshSubscribers
  _refreshSubscribers = []
  waiting.forEach(w => w.resolve(token))
}

/** Fail everyone still waiting. The missing half of the pair, and the whole bug. */
function failSubscribers(err: unknown) {
  const waiting = _refreshSubscribers
  _refreshSubscribers = []
  waiting.forEach(w => w.reject(err))
}

/**
 * How long we will wait for the refresh call before giving up on it.
 *
 * Peter, 2026-10-24: *"after some time, the menus hang... you click and nothing happens."*
 * The same symptom as 2026-09-02 and a different half of the same mechanism. The refresh below
 * is posted with the BARE axios, not the `api` instance, so it never inherited the 30s timeout
 * — on a stalled connection (and the link to the app server stalls) that promise simply never
 * settles. `_isRefreshing` then stays true for the life of the page, and every later 401 parks
 * behind a refresh that will never finish: not resolved, not rejected. The last fix made a
 * FAILED refresh tell everyone; this one makes sure the refresh always reaches an outcome.
 */
const REFRESH_TIMEOUT_MS = 15_000

export function initApiClient(
  opts: {
    accessToken: string
    refreshToken: string
    onLogout: () => void
    /**
     * Where to put the rotated pair.
     *
     * Refresh rotates both tokens, and they were written to the module and to localStorage but
     * never back to the store they came from. Any later `initApiClient` — `fetchMe()` makes
     * one — then overwrote the live tokens with the store's stale copies. Since the old refresh
     * token is spent by then, the next refresh fails outright and logs the session out.
     */
    onTokens?: (t: { accessToken: string; refreshToken: string }) => void
  },
) {
  _accessToken = opts.accessToken
  _refreshToken = opts.refreshToken
  _onLogout = opts.onLogout
  if (opts.onTokens) _onTokens = opts.onTokens
}

export function clearApiClient() {
  _accessToken = null
  _refreshToken = null
}

/**
 * Tell the server to revoke a refresh token, WITHOUT going through `api`.
 *
 * Signing out through the authenticated instance is circular: the logout call carries the
 * access token that has just been rejected, so the response interceptor sees a 401, starts
 * ANOTHER refresh with a token we are in the middle of discarding, waits for it, fails, and
 * calls `onLogout` again. Found 2026-10-25 while testing the idle hang — a session whose
 * refresh had failed kept a dead token in localStorage for at least one more refresh cycle
 * instead of returning the person to the login page.
 *
 * Bare axios, bounded, and failure is not the caller's problem: the local sign-out has
 * already happened by the time this runs, and a server that cannot be reached must not be
 * able to keep somebody signed in.
 */
export async function revokeRefreshToken(refreshToken: string): Promise<void> {
  try {
    await axios.post('/api/auth/logout', { refreshToken }, { timeout: REFRESH_TIMEOUT_MS })
  } catch {
    /* best effort — the token expires server-side regardless */
  }
}

export const api: AxiosInstance = axios.create({
  baseURL: '/api',
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000,
})

// Attach access token to every request
api.interceptors.request.use(config => {
  if (_accessToken) {
    config.headers['Authorization'] = `Bearer ${_accessToken}`
  }

  /**
   * Do not announce a JSON body we are not sending.
   *
   * Peter, 2026-10-24: *"ensure that the reconcile and flag button are doing what they are
   * supposed to be doing."* Neither did. Both returned 500, and so did eight other buttons
   * across the app, for one reason that is nothing to do with any of their handlers.
   *
   * The instance sets `Content-Type: application/json` as a default, so it rides on EVERY
   * request — including the bodyless ones, `apiPost('/x/flag')` and `apiPatch('/x/reconcile')`
   * among them. Fastify sees a request that declares a JSON body and carries none, and
   * refuses it before the route is ever reached. The handler is never called, so no amount of
   * reading the handler explains it.
   *
   * Measured on the reconcile endpoint: with the header and no body, 500; without the header,
   * 200; with the header and `{}`, 200. Ten call sites were affected — back up now, dismiss
   * checklist, compute churn, FUP reset, end roaming session, sync RADIUS, retry queue, revoke
   * login session, flag and reconcile.
   */
  if (config.data === undefined || config.data === null) {
    delete config.headers['Content-Type']
  }
  return config
})

// Handle 401 → refresh → retry, or log out
api.interceptors.response.use(
  res => res,
  async (error: AxiosError) => {
    const originalRequest = error.config

    if (error.response?.status === 401 && originalRequest && !originalRequest.headers['_retry']) {
      if (_isRefreshing) {
        return new Promise<string>((resolve, reject) => subscribeToRefresh({ resolve, reject }))
          .then(token => {
            originalRequest.headers['Authorization'] = `Bearer ${token}`
            return api(originalRequest)
          })
      }

      originalRequest.headers['_retry'] = 'true'
      _isRefreshing = true

      /**
       * No refresh token means there is nothing to refresh WITH.
       *
       * Without this the request below posts `{ refreshToken: null }`, waits for the round
       * trip, and fails — turning an instant, certain logout into a slow one. Worse, on a
       * slow link it leaves the parked requests hanging for the duration.
       */
      if (!_refreshToken) {
        _isRefreshing = false
        failSubscribers(error)
        _onLogout?.()
        return Promise.reject(error)
      }

      try {
        const resp = await axios.post<{ data: { accessToken: string; refreshToken: string } }>(
          '/api/auth/refresh',
          { refreshToken: _refreshToken },
          // Bounded, so this promise always reaches an outcome — see REFRESH_TIMEOUT_MS.
          { timeout: REFRESH_TIMEOUT_MS },
        )
        const { accessToken, refreshToken } = resp.data.data
        _accessToken = accessToken
        _refreshToken = refreshToken

        // Persist rotated tokens — to storage, and back to the store that supplied them.
        localStorage.setItem('accessToken', accessToken)
        localStorage.setItem('refreshToken', refreshToken)
        _onTokens?.({ accessToken, refreshToken })

        notifySubscribers(accessToken)
        _isRefreshing = false

        originalRequest.headers['Authorization'] = `Bearer ${accessToken}`
        return api(originalRequest)
      } catch (refreshErr) {
        _isRefreshing = false
        // THE FIX: everyone parked behind this refresh is told it failed, rather than being
        // left pending forever while the UI waits on promises that will never settle.
        failSubscribers(refreshErr)
        _onLogout?.()
        return Promise.reject(error)
      }
    }

    return Promise.reject(error)
  },
)

// Typed helper that extracts data or throws ApiError
export async function apiGet<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  const res = await api.get<{ data: T }>(url, { params })
  return res.data.data
}

/**
 * `stepUpTicket` is the one-use proof that a code was just entered in a confirmation dialog.
 *
 * It travels as a header rather than in the body so a guarded route's payload never has to
 * grow a field that has nothing to do with what the route does — and so `useStepUp` can retry
 * the *same* call with the ticket attached, rather than needing a second code path per action.
 */
export interface MutationOptions {
  stepUpTicket?: string | null
}

function stepUpHeaders(opts?: MutationOptions): { headers?: Record<string, string> } {
  return opts?.stepUpTicket ? { headers: { 'X-Step-Up': opts.stepUpTicket } } : {}
}

export async function apiPost<T>(url: string, body?: unknown, opts?: MutationOptions): Promise<T> {
  const res = await api.post<{ data: T }>(url, body, stepUpHeaders(opts))
  return res.data.data
}

export async function apiPut<T>(url: string, body?: unknown, opts?: MutationOptions): Promise<T> {
  const res = await api.put<{ data: T }>(url, body, stepUpHeaders(opts))
  return res.data.data
}

export async function apiPatch<T>(url: string, body?: unknown): Promise<T> {
  const res = await api.patch<{ data: T }>(url, body)
  return res.data.data
}

export async function apiDelete<T = { deleted: boolean }>(url: string): Promise<T> {
  const res = await api.delete<{ data: T }>(url)
  return res.data.data
}

/**
 * Turn a thrown value into a message fit for a toast.
 *
 * `fallback` is an optional caller-supplied, human-readable description of what
 * the failed operation was ("Failed to save binding"). It is used only when the
 * server did not send one through the standard error envelope, in preference to
 * axios' own generic text ("Request failed with status code 500"), which is
 * never useful to an operator. Omitting it preserves the original behaviour.
 */
/**
 * What the server says when an action needs a second factor it did not get.
 *
 * A 403 is normally "you may not do this" and ends in a toast. This one means "do it again
 * with a code", which is a dialog, not an error — so it has to be told apart from an ordinary
 * refusal by its code rather than by its status, and `reason` decides which of three different
 * things the dialog says.
 */
export interface StepUpChallenge {
  action: string
  reason: 'not_enrolled' | 'factor_not_used' | 'stale'
  enrolled: boolean
  windowSeconds: number
}

/** Returns the challenge when `err` is a step-up refusal, otherwise null. */
export function extractStepUp(err: unknown): StepUpChallenge | null {
  if (!axios.isAxiosError(err)) return null
  const data = err.response?.data as ApiError | undefined
  if (data?.error?.code !== 'STEP_UP_REQUIRED') return null
  const details = data.error.details as Partial<StepUpChallenge> | undefined
  return {
    action: details?.action ?? 'unknown',
    reason: details?.reason ?? 'stale',
    enrolled: details?.enrolled ?? false,
    windowSeconds: details?.windowSeconds ?? 300,
  }
}

export function extractApiError(err: unknown, fallback?: string): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as ApiError | undefined
    return data?.error?.message ?? fallback ?? err.message
  }
  if (err instanceof Error) return err.message
  return fallback ?? 'Unknown error'
}
